/**
 * resolves the computed value of CSS values using browser internals, so that
 * we can easily infer the value of CSS tokens at runtime
 *
 * @example
 * ```
 * const { bg, color } = resolveComputedCssValues({
 *   bg: colors.bg(),
 *   text: colors.text.primary(),
 * });
 * ```
 */
export function resolveComputedCssValues<T extends string>(
  input: Record<T, string>,
  target: HTMLElement = document.documentElement,
): Record<T, string> {
  const tmpPropName = `--tmp-${Date.now()}--resolve-css`;
  const keys = Object.keys(input) as T[];

  // apply styling as a temporary CSS value, so that we can resolve the actual
  // computed value using browser internals without modifying any actual CSS
  // layouts
  keys.forEach((key) => {
    target.style.setProperty(`${tmpPropName}--${key}`, input[key]);
  });

  // extract the computed value for each of the desired props
  const computedStyle = window.getComputedStyle(target);
  const output = keys.reduce(
    (acc, key) => {
      acc[key] = computedStyle.getPropertyValue(`${tmpPropName}--${key}`);

      return acc;
    },
    {} as Record<T, string>,
  );

  // finally clean up the temporary CSS properties so we don't bloat the DOM
  // unnecessarily
  keys.forEach((key) => {
    target.style.removeProperty(`${tmpPropName}--${key}`);
  });

  return output;
}
